import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import { useHistory } from 'react-router-dom';
import Spinner from '../../components/common/spinner';
import SwitchBar from '../../components/switch-bar';
import RightArrow from '../../assets/images/left-arrow-symbol.svg';
import {
  TagGroup,
  useCreateTagGroup,
  useDeleteTagGroup,
  useTagGroups,
  useUpdateTagGroup,
} from '../../hooks/useTagGroups';
import { Paginator } from '../../components/common/paginator';
import AlertBox from '../../components/route-leaving-prompt/alert-box';
import Modal from '../../components/common/modal';
import { useForm } from 'react-hook-form';
import { Tag, TagExtended, TagType, useTags } from '../../hooks/useTags';
import { CustomInput, UseFormDropDown } from '../../components/common';
import { onBlur } from '../../components/common/useForm';
import Searchbar from '../../components/common/searchbar';

export const HeaderOptions = [
  { heading: 'Tag Groups', path: '/tags/tag-groups' },
  { heading: 'Tags', path: '/tags' },
];

export const TagTypes = {
  All: undefined,
  Topic: 'topic',
  Trait: 'trait',
} as const;

interface TagGroupFilters {
  limit: number;
  keys: string;
  page: number;
  name: string;
  type?: TagType;
}

const TagGroups: React.FunctionComponent = (): JSX.Element => {
  const [filters, setFilters] = useState<TagGroupFilters>({
    limit: 25,
    keys: 'all',
    name: '',
    page: 1,
    type: undefined,
  });
  const { page, limit, ...filteredFilters } = filters;
  const { data: tagGroupsList, isLoading } = useTagGroups(filteredFilters);
  const { mutate: deleteTagGroup } = useDeleteTagGroup();
  const [selectedTagGroup, setSelectedTagGroup] = useState<TagGroup | undefined>(tagGroupsList?.[0] || undefined);
  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState<boolean>(false);

  // front end pagination
  const startIndex = (filters.page - 1) * filters.limit;
  const endIndex = startIndex + filters.limit;
  const paginatedTagsList = tagGroupsList?.slice(startIndex, endIndex);

  const handleDelete = () => {
    setShowAlertModal(false);
    deleteTagGroup(selectedTagGroup?.id!);
  };

  const handleSelectedTagGroup = (tagGroup?: TagGroup) => {
    if (tagGroup) {
      setSelectedTagGroup(tagGroup);
    } else if (paginatedTagsList && paginatedTagsList.length > 0) {
      setSelectedTagGroup(paginatedTagsList[0]);
    }
  };

  if (selectedTagGroup && !tagGroupsList?.includes(selectedTagGroup)) {
    setSelectedTagGroup(undefined);
  }

  if (!selectedTagGroup && tagGroupsList && tagGroupsList?.length > 0) {
    handleSelectedTagGroup();
  }

  return (
    <div>
      <Header heading={'Tags'} />
      <SwitchBar heading={HeaderOptions} position={0} />
      <Filters
        count={tagGroupsList?.length || undefined}
        filters={filters}
        setFilters={setFilters}
        setSelectedTagGroup={setSelectedTagGroup}
        setShowCreateTagModal={setShowCreateTagModal}
      />
      {isLoading && <Spinner />}
      {tagGroupsList && selectedTagGroup && (
        <div className="flex w-full">
          <TagGroupsList
            selectedTagGroupId={selectedTagGroup.id}
            setSelectedTagGroup={setSelectedTagGroup}
            tagGroups={paginatedTagsList || []}
          />
          <TagGroupDetails
            tagGroup={selectedTagGroup}
            setShowAlertModal={setShowAlertModal}
            setSelectedTagGroup={setSelectedTagGroup}
          />
        </div>
      )}
      {showCreateTagModal && <CreateTagModal setShowCreateTagModal={setShowCreateTagModal} />}
      <AlertBox
        visible={showAlertModal}
        handlePrompt={handleDelete}
        closeModal={() => setShowAlertModal(false)}
        titleText={'Alert'}
        contentText={`Are you sure you want to remove the selected Tag Group?`}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Remove'}
      />
    </div>
  );
};

interface TagGroupsListProps {
  selectedTagGroupId: number;
  setSelectedTagGroup: (tagGroup: TagGroup) => void;
  tagGroups: TagGroup[];
}

const TagGroupsList: React.FC<TagGroupsListProps> = ({ selectedTagGroupId, setSelectedTagGroup, tagGroups }) => {
  return (
    <div className="w-1/2 px-7">
      <div className="">
        <div className="text-gray-dark sticky mb-2 grid h-12 w-full grid-cols-12 items-center border-b-2 border-black text-left font-bold">
          <div className="col-span-5 font-bold">
            <p className="pl-10">Tag Group</p>
          </div>
          <div className="col-span-7">Type</div>
        </div>
      </div>
      <div className="pb-10">
        {tagGroups?.slice().map((tagGroup: TagGroup) => (
          <div
            className={`w-full ${selectedTagGroupId === tagGroup.id ? 'bg-blue-primary text-white' : 'text-gray-dark bg-gray-200'} my-2 grid h-12 cursor-pointer grid-cols-12 items-center rounded-xl border-y text-left`}
            onClick={() => {
              setSelectedTagGroup(tagGroup);
            }}
          >
            <div className="col-span-5 font-bold">
              <p className="pl-10">{tagGroup.name}</p>
            </div>
            <div className="col-span-7 mr-2 flex items-center justify-between">
              <div className="col-span-3 capitalize"> {tagGroup.tag_group_type} </div>
              <button className="blue-primary h-8 w-8 rotate-180 transform rounded-full">
                <img className="h-8 w-8" src={RightArrow} alt="left Arrow"></img>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface TagGroupDetailsProps {
  tagGroup: TagGroup;
  setShowAlertModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTagGroup: React.Dispatch<React.SetStateAction<TagGroup | undefined>>;
}

const TagGroupDetails: React.FC<TagGroupDetailsProps> = ({ tagGroup, setShowAlertModal, setSelectedTagGroup }) => {
  const { data: tags } = useTags({ include_hidden: true });
  const { mutate: updateTagGroup } = useUpdateTagGroup();
  const [tagsList, setTagsList] = useState<TagExtended[]>([]);
  const [tagsModal, setTagsModal] = useState<boolean>(false);

  const processTags = (tags: Tag[]) => {
    if (!tagGroup.tags) {
      setTagsList(tags.map((tag) => ({ ...tag, hasTag: false })));
      return;
    } else {
      const tagIds = tagGroup.tags.map((tag) => tag.id);
      setTagsList(tags.map((tag) => ({ ...tag, hasTag: tagIds.includes(tag.id) })));
    }
  };

  const defaultTagGroup = {
    ...tagGroup,
    children: tagGroup.children || null,
    description: tagGroup.description || null,
    key: tagGroup.key || null,
    name: tagGroup.name || null,
    parent_id: tagGroup.parent_id || null,
    tag_group_type: tagGroup.tag_group_type || null,
    tags: tagGroup?.tags || [],
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: useMemo(() => {
      if (tagGroup) {
        return defaultTagGroup;
      }
    }, [tagGroup]),
  });

  const handleTag = (hasTag: boolean, id: number) => {
    const updatedTagsList = tagsList.map((tag) => (tag.id === id ? { ...tag, hasTag } : tag));
    setTagsList(updatedTagsList);
    setValue('tags', updatedTagsList, { shouldDirty: true });
  };

  const onSubmit = (data: any) => {
    const payload = {
      ...data,
      tags: tagsList.filter((tag: TagExtended) => tag.hasTag),
    };
    updateTagGroup({ tagGroupId: tagGroup.id, tagGroup: payload });
    setSelectedTagGroup(undefined);
  };

  useEffect(() => {
    if (tagGroup && tags) {
      reset(tagGroup);
      processTags(tags);
    }
  }, [tagGroup, tags, reset]);

  return (
    <div className="mx-7 my-12 w-1/2 rounded-3xl border-2 border-gray-200">
      <div className="flex h-10 items-center justify-between px-6 pt-2">
        <button
          className="bg-red h-6 w-20 rounded-full px-2 text-center text-sm text-white"
          onClick={() => {
            setShowAlertModal(true);
          }}
        >
          REMOVE
        </button>
        <div className={` ${!isDirty && 'cursor-not-allowed'}`}>
          <button
            className={`ml-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white ${
              isDirty ? 'bg-bright-blue' : 'bg-gray-background-dark pointer-events-none'
            }`}
            type="submit"
            form="update-tag-group"
          >
            SAVE
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} id="update-tag-group">
        <div className="w-full px-3">
          <div className="mt-5 grid w-full grid-cols-1 gap-x-6 gap-y-6 px-7">
            <div className="flex flex-col space-y-4">
              <CustomInput
                inputType="text"
                Label="Tag Group Name"
                register={{
                  ...register('name', {
                    required: 'Please provide Tag name',
                    onBlur: (e: any) => onBlur(e, 'name', setValue),
                  }),
                }}
                Error={errors?.name?.message?.length ? true : false}
                ErrorMessage={errors.name?.message}
                dataTestId="tag-group-name"
              />
              <>
                <p>Type</p>
                <p className="font-bold capitalize">{tagGroup.tag_group_type}</p>
              </>
            </div>
            <div>
              <div className="text-gray-dark border-gray-dark mb-2 flex h-10 items-center justify-between border-b py-1 leading-4">
                <p>Tags Associated with Group</p>
                <button
                  className="bg-gray-dark rounded-full px-3 py-2 text-white"
                  type="button"
                  onClick={() => {
                    setTagsModal(true);
                  }}
                  data-testid="add-tag-button"
                >
                  + ADD
                </button>
              </div>
              <div className="grid grid-cols-2 gap-x-2">
                {tagsList?.filter((tag: TagExtended) => tag.hasTag).length > 0 ? (
                  tagsList
                    .filter((tag: TagExtended) => tag.hasTag)
                    .map((item: TagExtended) => {
                      return (
                        <button className={`bg-blue-primary mx-2 my-1 w-full text-sm font-bold text-white`}>
                          <div className="flex justify-between px-3 py-2">
                            <div className="pr-2">{item.name}</div>
                            <button
                              type="button"
                              className="font-bold text-white"
                              onClick={(e) => {
                                e.preventDefault();
                                handleTag(false, item.id);
                              }}
                              data-testid={`remove-tag-button-${item.id}`}
                            >
                              X
                            </button>
                          </div>
                        </button>
                      );
                    })
                ) : (
                  <p className="m-2"> No Listener Tags </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
      <Modal
        isModel={tagsModal}
        InnerComponent={
          <div className="inline-block max-w-3xl transform overflow-hidden rounded-lg bg-white text-left align-bottom transition-all">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <div className="flex justify-between">
                    <h3 className="mb-3 text-lg font-bold leading-6 text-gray-900" id="modal-title">
                      Add Tag(s) to Group
                    </h3>
                    <button
                      className="bg-bright-blue h-6 w-20 rounded-full px-2 text-center text-sm text-white"
                      onClick={() => {
                        setTagsModal(false);
                      }}
                      data-testid="close-tag-group-modal"
                    >
                      CLOSE
                    </button>
                  </div>
                  <h3 className="mb-3 text-lg leading-6 text-gray-900" id="modal-title">
                    Select tag(s) from the list below to add them to this group.
                  </h3>
                  <div className="max-window-height-audio-reviews grid grid-cols-2 gap-x-2 overflow-y-scroll">
                    {tagsList?.length > 0 &&
                      tagsList
                        .filter((tag: any) => tag.tag_type.toLowerCase() === tagGroup.tag_group_type)
                        .sort((a: any, b: any) => a.name.localeCompare(b.name))
                        .map((item: any) => {
                          return (
                            <button
                              className={`${
                                item.hasTag ? 'bg-blue-primary text-white' : 'bg-gray-background-light text-black'
                              } mt-2 space-y-2 text-sm font-bold`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleTag(!item.hasTag, item.id);
                              }}
                              data-testid={`add-tag-button-${item.id}`}
                            >
                              <div className="px-3 py-2">{item.name}</div>
                            </button>
                          );
                        })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

interface CreateTagModalProps {
  setShowCreateTagModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateTagModal: React.FC<CreateTagModalProps> = ({ setShowCreateTagModal }) => {
  const { mutate: createTagGroup } = useCreateTagGroup();

  const defaultNewTag = {
    name: '',
    tag_group_type: 'topic',
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: defaultNewTag,
  });

  const onSubmit = (data: any) => {
    createTagGroup(data);
    setShowCreateTagModal(false);
    reset(defaultNewTag);
  };

  return (
    <>
      <Modal
        isModel={true}
        InnerComponent={
          <div className="inline-block max-w-3xl transform overflow-hidden rounded-lg border-2 border-black bg-white text-left transition-all">
            <div className="flex h-10 items-center justify-between px-6 pt-2">
              <div>Create New Tag Group</div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} id="create-tag-group">
              <div className="w-full px-3">
                <div className="mt-5 grid w-full grid-cols-1 gap-x-6 gap-y-6 px-7">
                  <div className="flex flex-col space-y-4">
                    <CustomInput
                      inputType="text"
                      Label="Tag Group Name"
                      register={{
                        ...register('name', {
                          required: 'Please provide Tag name',
                          onBlur: (e: any) => onBlur(e, 'name', setValue),
                        }),
                      }}
                      Error={errors?.name?.message?.length ? true : false}
                      ErrorMessage={errors.name?.message}
                      dataTestId="tag-group-name"
                    />
                    <UseFormDropDown label={'Status'}>
                      <select
                        className="select-box w-full focus:ring-transparent"
                        defaultValue="published"
                        {...register('tag_group_type')}
                        data-testid="tag-group-type"
                      >
                        <option value="topic">Topic</option>
                        <option value="trait">Trait</option>
                      </select>
                    </UseFormDropDown>
                  </div>
                </div>
              </div>
            </form>
            <div className="flex w-full items-center justify-evenly py-2">
              <button
                className="bg-red h-6 w-20 rounded-full px-2 text-center text-sm text-white"
                onClick={() => {
                  reset(defaultNewTag);
                  setShowCreateTagModal(false);
                }}
                data-testid="tag-group-create-cancel-button"
              >
                CANCEL
              </button>
              <div className={` ${!isDirty && 'cursor-not-allowed'}`}>
                <button
                  className={`ml-2 h-6 w-20 rounded-full px-2 text-center text-sm text-white ${
                    isDirty ? 'bg-bright-blue' : 'bg-gray-background-dark pointer-events-none'
                  }`}
                  type="submit"
                  form="create-tag-group"
                  data-testid="tag-group-create-save-button"
                >
                  SAVE
                </button>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

interface FilterProps {
  count?: number;
  filters: TagGroupFilters;
  setFilters: React.Dispatch<React.SetStateAction<TagGroupFilters>>;
  setSelectedTagGroup: React.Dispatch<React.SetStateAction<TagGroup | undefined>>;
  setShowCreateTagModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Filters: React.FunctionComponent<FilterProps> = ({
  count,
  filters,
  setFilters,
  setSelectedTagGroup,
  setShowCreateTagModal,
}): JSX.Element => {
  return (
    <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center justify-between border-b px-7">
      <div className="w-1/4">
        <Searchbar
          search={(data) => {
            setFilters((filters) => {
              return { ...filters, page: 1, name: data };
            });
          }}
        />
      </div>
      <div className="w-1/2">
        <Paginator
          currentPage={filters.page}
          count={count || 0}
          limit={filters.limit}
          handlePageChange={(page: number) => {
            setSelectedTagGroup(undefined);
            setFilters((filter) => {
              return { ...filter, page: page };
            });
          }}
        />
      </div>
      <div className="w-1/3">
        <div className="flex">
          <div className="gray-background-dark flex items-center justify-between py-4">
            {Object.keys(TagTypes).map((item) => (
              <button
                data-testid={`tag-group-type-${item}`}
                className={
                  'px-5 py-1 text-sm ' +
                  (TagTypes[item as keyof typeof TagTypes] === filters.type
                    ? 'bg-blue-primary text-white'
                    : 'bg-gray-background-dark text-gray-dark')
                }
                onClick={() => {
                  setFilters((filter) => {
                    return { ...filter, type: TagTypes[item as keyof typeof TagTypes], page: 1 };
                  });
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-1/3">
        <div className="flex h-8 w-full justify-center">
          <button
            className="h-full rounded-full bg-white px-2 py-1 text-center text-sm"
            onClick={() => {
              setShowCreateTagModal(true);
            }}
            data-testid="add-tag-group-button"
          >
            Add Tag Group
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagGroups;
